/**
 * Turns what OCR read into suggestions from the user's own data (roadmap step 12). No AI: only
 * merchants the user has already used, aliases learned from the user's corrections, and categories
 * the user already chose. Never returns a category that is not in the user's (active) list.
 */
import { merchantSimilarity, normalizeMerchant } from "./merchants";
import type { Confidence, PaymentHint, ReceiptReading } from "./ocr/receipt-parser";

/** Learned from a correction: what OCR reads for a shop → the user's name and category for it. */
export interface MerchantAlias {
  /** `normalizeMerchant` of the OCR text. */
  id: string;
  ocrText: string;
  merchant: string;
  /** Set when the user corrected a suggested category: a merchant rule, which wins over history. */
  categoryId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface KnownExpense {
  merchant: string | null;
  categoryId: string;
  purchaseDate: string;
  updatedAt: string;
}

export interface ReceiptSuggestion {
  merchant: { value: string; confidence: Confidence; source: "alias" | "history" | "ocr" } | null;
  category: { categoryId: string; source: "merchant_rule" | "merchant_history" } | null;
  paymentMethod: { paymentMethodId: string; confidence: Confidence } | null;
}

/** Seeded payment methods (src/data/reference.ts). */
export const PAYMENT_HINT_METHODS: Record<PaymentHint, string> = {
  cash: "pm-contanti",
  card: "pm-carta-unicredit",
  satispay: "pm-satispay",
  postepay: "pm-postepay",
};

/** Merchants this similar to a known one are shown with the user's spelling. */
export const KNOWN_MERCHANT_SIMILARITY = 0.8;
const HISTORY_DAYS = 365;

export interface RecognitionContext {
  aliases: ReadonlyMap<string, MerchantAlias>;
  /** Confirmed expenses with a merchant (any order). */
  expenses: readonly KnownExpense[];
  activeCategoryIds: ReadonlySet<string>;
  activePaymentMethodIds: ReadonlySet<string>;
  today: string;
}

const newestFirst = (a: KnownExpense, b: KnownExpense) => b.purchaseDate.localeCompare(a.purchaseDate) || b.updatedAt.localeCompare(a.updatedAt);

export function recognizeReceipt(reading: ReceiptReading, ctx: RecognitionContext): ReceiptSuggestion {
  const ocrMerchant = reading.merchant;
  const key = normalizeMerchant(ocrMerchant?.value);
  const alias = key ? ctx.aliases.get(key) : undefined;
  const withMerchant = ctx.expenses.filter((e) => e.merchant);

  let merchant: ReceiptSuggestion["merchant"] = null;
  if (alias) merchant = { value: alias.merchant, confidence: "high", source: "alias" };
  else if (ocrMerchant) {
    // The closest merchant the user already typed, newest spelling first.
    let best: { e: KnownExpense; score: number } | null = null;
    for (const e of [...withMerchant].sort(newestFirst)) {
      const score = merchantSimilarity(ocrMerchant.value, e.merchant) ?? 0;
      if (score >= KNOWN_MERCHANT_SIMILARITY && (!best || score > best.score)) best = { e, score };
    }
    merchant = best ? { value: best.e.merchant!, confidence: "high", source: "history" } : { value: ocrMerchant.value, confidence: ocrMerchant.confidence, source: "ocr" };
  }

  let category: ReceiptSuggestion["category"] = null;
  if (alias?.categoryId && ctx.activeCategoryIds.has(alias.categoryId)) category = { categoryId: alias.categoryId, source: "merchant_rule" };
  else if (merchant && merchant.source !== "ocr") {
    const merchantKey = normalizeMerchant(merchant.value);
    const since = new Date(Date.parse(ctx.today) - HISTORY_DAYS * 86_400_000).toISOString().slice(0, 10);
    const last = withMerchant
      .filter((e) => e.purchaseDate >= since && normalizeMerchant(e.merchant) === merchantKey && ctx.activeCategoryIds.has(e.categoryId))
      .sort(newestFirst)[0];
    if (last) category = { categoryId: last.categoryId, source: "merchant_history" };
  }

  let paymentMethod: ReceiptSuggestion["paymentMethod"] = null;
  if (reading.payment) {
    const id = PAYMENT_HINT_METHODS[reading.payment.value];
    if (ctx.activePaymentMethodIds.has(id)) paymentMethod = { paymentMethodId: id, confidence: reading.payment.confidence };
  }
  return { merchant, category, paymentMethod };
}

/**
 * What to learn when the user saves an expense read from a receipt: an alias when the saved
 * merchant differs from the suggestion, and a merchant rule when the suggested category was
 * changed. Returns null when there is nothing new to learn.
 */
export function learnAlias(
  ocrMerchantText: string | null,
  suggestion: ReceiptSuggestion | null,
  saved: { merchant: string | null; categoryId: string },
  existing: MerchantAlias | undefined,
  now: string,
): MerchantAlias | null {
  const key = normalizeMerchant(ocrMerchantText);
  if (!key || !ocrMerchantText) return null;
  const proposed = suggestion?.merchant?.value ?? ocrMerchantText;
  const merchantCorrected = saved.merchant !== null && normalizeMerchant(saved.merchant) !== null && normalizeMerchant(saved.merchant) !== normalizeMerchant(proposed);
  const categoryCorrected = suggestion?.category != null && suggestion.category.categoryId !== saved.categoryId;
  if (!merchantCorrected && !categoryCorrected) return null;
  return {
    id: key,
    ocrText: ocrMerchantText,
    merchant: merchantCorrected ? saved.merchant! : (existing?.merchant ?? saved.merchant ?? proposed),
    categoryId: categoryCorrected ? saved.categoryId : (existing?.categoryId ?? null),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };
}
